import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X, Share, Plus, MonitorSmartphone, ChevronRight } from 'lucide-react';

const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;
const BANNER_DISMISS_KEY = 'pwa-banner-cliente-dismissed';
const BANNER_DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true ||
  document.referrer.includes('android-app://');

const detectPlatform = () => {
  const ua = window.navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isMobile = isIOS || isAndroid || /Mobile/.test(ua);
  return { isIOS, isAndroid, isMobile };
};

export const InstallAppBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const { isIOS, isMobile } = detectPlatform();

  useEffect(() => {
    if (isStandalone()) return;
    const dismissed = localStorage.getItem(BANNER_DISMISS_KEY);
    if (dismissed && Date.now() - parseInt(dismissed) < BANNER_DISMISS_MS) return;
    setVisible(true);
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    const onInstalled = () => setVisible(false);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setVisible(false);
      setDeferredPrompt(null);
    } else {
      setShowGuide(g => !g);
    }
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(BANNER_DISMISS_KEY, Date.now().toString());
  };

  if (!visible) return null;

  return (
    <div className="rounded-2xl border border-primary/25 bg-primary/5 overflow-hidden mb-6" data-testid="client-install-banner">
      <div className="p-4 flex items-center gap-3 flex-wrap">
        <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <MonitorSmartphone className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="font-semibold text-sm">Salva l'app sul telefono</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isMobile
              ? 'Documenti, fatture, date del cantiere e assistenza sempre a portata di tocco, come una vera app.'
              : "Installa l'app sul tuo dispositivo per accedere con un click a documenti, fatture e assistenza."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={install} data-testid="client-install-button">
            <Download className="w-3.5 h-3.5 mr-1" />{deferredPrompt ? 'Installa ora' : 'Come si installa'}
          </Button>
          <button onClick={dismiss} aria-label="Chiudi" className="text-muted-foreground hover:text-foreground min-w-[24px] min-h-[24px] flex items-center justify-center" data-testid="client-install-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {showGuide && (
        <div className="border-t border-primary/15 bg-card/60 p-4 text-xs" data-testid="client-install-guide">
          {isIOS ? (
            <ol className="space-y-2">
              <li className="flex items-center gap-2"><span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">1</span>Tocca il pulsante <Share className="w-4 h-4 inline text-primary" /> <strong>Condividi</strong> nella barra in basso di Safari.</li>
              <li className="flex items-center gap-2"><span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">2</span>Scorri e tocca <strong>«Aggiungi a Home»</strong> <Plus className="w-4 h-4 inline text-primary" />.</li>
              <li className="flex items-center gap-2"><span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">3</span>Conferma con <strong>«Aggiungi»</strong>. Fatto! 🎉</li>
            </ol>
          ) : (
            <ol className="space-y-2">
              <li className="flex items-center gap-2"><span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">1</span>Apri il menu del browser (<strong>⋮</strong>) e tocca <strong>«Aggiungi a schermata Home»</strong> o <strong>«Installa app»</strong>.</li>
              <li className="flex items-center gap-2"><span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">2</span>Conferma con <strong>«Installa»</strong>. Fatto! 🎉</li>
            </ol>
          )}
        </div>
      )}
    </div>
  );
};

export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const { isIOS, isMobile } = detectPlatform();

  useEffect(() => {
    if (isStandalone()) return;

    const dismissed = localStorage.getItem(DISMISS_KEY);
    const recentlyDismissed = dismissed && Date.now() - parseInt(dismissed) < DISMISS_MS;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!recentlyDismissed) setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const onInstalled = () => { setShowBanner(false); setDeferredPrompt(null); };
    window.addEventListener('appinstalled', onInstalled);

    // iOS non emette beforeinstallprompt: mostra il banner con le istruzioni manuali
    let iosTimer;
    if (isIOS && !recentlyDismissed) {
      iosTimer = setTimeout(() => setShowBanner(true), 2500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setShowBanner(false);
      setDeferredPrompt(null);
    } else {
      // Nessun prompt nativo disponibile (iOS o browser senza supporto): mostra la guida
      setShowGuide(true);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowGuide(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-40 animate-in slide-in-from-bottom-4 duration-300" data-testid="pwa-install-prompt">
      <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <MonitorSmartphone className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">Installa l'app RevoAPP Master</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isMobile
                  ? 'Aggiungila alla schermata home per un accesso rapido, come una vera app.'
                  : 'Installala sul tuo PC per aprirla in un click dalla barra delle applicazioni.'}
              </p>

              {!showGuide && (
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={handleInstall} className="button-press" data-testid="pwa-install-button">
                    <Download className="w-3.5 h-3.5 mr-1" />
                    {deferredPrompt ? 'Installa ora' : 'Come si installa'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleDismiss} data-testid="pwa-dismiss-button">
                    Non ora
                  </Button>
                </div>
              )}
            </div>
            <button onClick={handleDismiss} aria-label="Chiudi" className="text-muted-foreground hover:text-foreground min-w-[24px] min-h-[24px] flex items-center justify-center" data-testid="pwa-close-button">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {showGuide && (
          <div className="border-t border-border bg-muted/30 p-4 text-xs space-y-3" data-testid="pwa-install-guide">
            {isIOS ? (
              <>
                <p className="font-medium text-sm">Su iPhone / iPad (Safari):</p>
                <ol className="space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">1</span>
                    Tocca il pulsante <Share className="w-4 h-4 inline text-primary" /> <strong>Condividi</strong> nella barra in basso.
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">2</span>
                    Scorri e tocca <strong>«Aggiungi a Home»</strong> <Plus className="w-4 h-4 inline text-primary" />.
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">3</span>
                    Conferma con <strong>«Aggiungi»</strong>. Fatto! 🎉
                  </li>
                </ol>
              </>
            ) : (
              <>
                <p className="font-medium text-sm">Su computer (Chrome / Edge):</p>
                <ol className="space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">1</span>
                    Clicca l'icona <Download className="w-4 h-4 inline text-primary" /> (Installa) nella barra degli indirizzi, a destra.
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">2</span>
                    In alternativa: menu <strong>⋮</strong> <ChevronRight className="w-3 h-3 inline" /> <strong>«Installa RevoAPP Master»</strong>.
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">3</span>
                    Conferma con <strong>«Installa»</strong>. Fatto! 🎉
                  </li>
                </ol>
              </>
            )}
            <Button size="sm" variant="outline" className="w-full mt-1" onClick={handleDismiss} data-testid="pwa-guide-close-button">
              Ho capito
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
